import { createFeatureSelector, createSelector } from '@ngrx/store';

import { LocationAcl } from '@era/locations-sdk';
import { Group, User } from '@era/security-sdk';
import { Dictionary } from '@ngrx/entity';
import type { NamedLocationAcl, NamedLocationAclGroups, NamedLocationAclMembers } from '@rocksoft/shared';
import * as groupSelector from '../group/group.selector';
import * as aclRouterSelector from '../router/acl-router.selector';
import * as locationsRouterSelector from '../router/locations-router.selector';
import * as userSelector from '../user/user.selector';
import * as fromLocationAcl from './location-acl.reducer';
import { LocationAclState } from './location-acl.state';

export const selectLocationAclState = createFeatureSelector<LocationAclState>('locationAcls');

export const selectLocationAclIds = createSelector(selectLocationAclState, fromLocationAcl.selectLocationAclIds);

export const selectAllLocationAcls = createSelector(selectLocationAclState, fromLocationAcl.selectAllLocationAcls);

export const selectLocationAcls = createSelector(selectLocationAclState, fromLocationAcl.selectLocationAcls);

export const selectLocationAclLoaded = createSelector(
  selectLocationAcls,
  (locationAcls: Dictionary<LocationAcl>) => locationAcls && Object.keys(locationAcls).length > 0
);

export const selectLocationAcl = (locationId: string) =>
  createSelector(selectLocationAcls, (locationAcls: Dictionary<LocationAcl>) =>
    locationAcls ? locationAcls[locationId] : undefined
  );

export const selectCurrentLocationAcl = createSelector(
  selectLocationAcls,
  locationsRouterSelector.selectLocationId,
  (locationAcls: Dictionary<LocationAcl>, locationId: string) => (locationAcls ? locationAcls[locationId] : undefined)
);

export const selectCurrentLocationAclGroup = createSelector(
  selectCurrentLocationAcl,
  aclRouterSelector.selectCurrentAclId,
  (locationAcl, aclGroupId) =>
    locationAcl && aclGroupId ? (locationAcl.groups ?? []).find((x) => x.groupId === aclGroupId) : undefined
);

export const selectCurrentLocationAclMember = createSelector(
  selectCurrentLocationAcl,
  aclRouterSelector.selectCurrentAclId,
  (locationAcl, aclUserId) =>
    locationAcl && aclUserId ? (locationAcl.users ?? []).find((x) => x.member === aclUserId) : undefined
);

export const selectCurrentLocationAclWithNames = createSelector(
  selectCurrentLocationAcl,
  userSelector.selectAllUsers,
  groupSelector.selectAllGroups,
  (locationAcl: LocationAcl, users: User[], groups: Group[]) => {
    const isLocationAclValue = (value: unknown): value is LocationAcl => {
      return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        'resourceId' in value &&
        'groups' in value &&
        'users' in value
      );
    };
    const findUserName = (id: string) => {
      const user = users.find((x) => x.id === id);
      return user ? user.displayName : undefined;
    };

    const findGroupName = (id: string) => {
      const group = groups.find((x) => x.id === id);
      return group ? group.displayName : undefined;
    };

    if (isLocationAclValue(locationAcl) && users && groups) {
      const locationAclGroups: NamedLocationAclGroups[] = (locationAcl.groups ?? []).map((x) => ({
        ...x,
        name: findGroupName(x.groupId) ?? '',
      }));
      const locationAclMembers: NamedLocationAclMembers[] = (locationAcl.users ?? []).map((x) => ({
        ...x,
        name: findUserName(x.member) ?? '',
      }));

      const result: NamedLocationAcl = {
        ...locationAcl,
        resourceId: locationAcl.resourceId ?? '',
        groups: locationAclGroups,
        users: locationAclMembers,
      };

      return result;
    }
    return undefined;
  }
);

export const selectCurrentLocationAclGroups = (groupIds: string[]) =>
  createSelector(selectCurrentLocationAclWithNames, (locationAcl) => {
    if (locationAcl && groupIds) {
      const locationAclGroups = (locationAcl.groups ?? []).filter((x) => groupIds.includes(x.groupId));
      return locationAclGroups ?? undefined;
    }

    return undefined;
  });
